import { updateConsent } from './gtm';
import { COOKIES_VERSION } from '../data/legalEntity';

export const CONSENT_STORAGE_KEY = 'aipsyhelp_cmp_v2';
export const CONSENT_EVENT = 'aipsyhelp:consent-change';

const DEFAULT_DENIED = {
  essential: true,
  analytics: false,
  advertising: false
};

function safeParse(raw) {
  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

export function getConsentRecord() {
  if (typeof window === 'undefined') return null;
  const modern = safeParse(window.localStorage.getItem(CONSENT_STORAGE_KEY));
  if (modern && modern.version) return modern;

  const legacyAccepted = window.localStorage.getItem('cookieConsentAccepted');
  if (legacyAccepted === null) return null;
  const legacySettings = safeParse(window.localStorage.getItem('cookieConsentSettings')) || {};
  return {
    version: 'legacy',
    decided: true,
    accepted: legacyAccepted === 'true',
    categories: {
      essential: true,
      analytics: Boolean(legacySettings.analytics),
      advertising: Boolean(legacySettings.advertising)
    },
    decidedAt: window.localStorage.getItem('cookieConsentDate') || null
  };
}

export function hasConsentDecision() {
  const record = getConsentRecord();
  return Boolean(record && record.decided);
}

export function getConsentCategories() {
  const record = getConsentRecord();
  if (!record) return { ...DEFAULT_DENIED };
  return { ...DEFAULT_DENIED, ...(record.categories || {}) };
}

export function isAnalyticsAllowed() {
  return getConsentCategories().analytics === true;
}

export function isAdvertisingAllowed() {
  return getConsentCategories().advertising === true;
}

export function persistConsent(categories, source = 'banner') {
  const next = {
    version: COOKIES_VERSION,
    decided: true,
    accepted: Boolean(categories.analytics || categories.advertising),
    categories: {
      essential: true,
      analytics: Boolean(categories.analytics),
      advertising: Boolean(categories.advertising)
    },
    source,
    decidedAt: new Date().toISOString()
  };

  window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(next));
  window.localStorage.setItem('cookieConsentAccepted', next.accepted ? 'true' : 'false');
  window.localStorage.setItem('cookieConsentDate', next.decidedAt);
  window.localStorage.setItem(
    'cookieConsentSettings',
    JSON.stringify({ analytics: next.categories.analytics, advertising: next.categories.advertising })
  );

  applyConsentToTags(next.categories);
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: next }));
  return next;
}

// Non-essential cookies that must not survive a reject / withdrawal.
// Third-party ones (clarity.ms CLID/MUID, bing.com MUID) can only be cleared
// by the vendor; clarity('consent', false) asks Clarity to erase its own.
const ANALYTICS_COOKIE_PREFIXES = ['_ga', '_clck', '_clsk'];
const ADVERTISING_COOKIE_PREFIXES = ['_gcl_'];

function cookieDomains() {
  const host = window.location.hostname;
  const domains = ['', host, `.${host}`];
  const parts = host.split('.');
  // Parent domains, e.g. .psyhelp.info for ai.psyhelp.info (never a bare TLD).
  for (let i = 1; i < parts.length - 1; i += 1) {
    domains.push(`.${parts.slice(i).join('.')}`);
  }
  return domains;
}

function deleteCookiesByPrefix(prefixes) {
  if (typeof document === 'undefined') return;
  const names = document.cookie
    .split(';')
    .map((pair) => pair.split('=')[0].trim())
    .filter((name) => name && prefixes.some((prefix) => name.startsWith(prefix)));
  const domains = cookieDomains();
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; path=/';
  names.forEach((name) => {
    domains.forEach((domain) => {
      document.cookie = `${name}=; ${expired}${domain ? `; domain=${domain}` : ''}`;
    });
  });
}

function callClarity(...args) {
  try {
    if (typeof window.clarity === 'function') window.clarity(...args);
  } catch (_) {
    /* Clarity not loaded or blocked: nothing to do */
  }
}

// Keep Clarity and first-party analytics/ads cookies in line with the choice.
// Runs on every banner decision, preference change and stored-choice replay.
function syncNonEssentialStorage(categories) {
  if (typeof window === 'undefined') return;
  if (categories.analytics) {
    callClarity('consent');
  } else {
    callClarity('consent', false);
    callClarity('stop');
    deleteCookiesByPrefix(ANALYTICS_COOKIE_PREFIXES);
    try {
      window.sessionStorage.removeItem('_cltk');
    } catch (_) {
      /* storage blocked */
    }
  }
  if (!categories.advertising) {
    deleteCookiesByPrefix(ADVERTISING_COOKIE_PREFIXES);
  }
}

export function applyConsentToTags(categories = getConsentCategories()) {
  if (categories.analytics && categories.advertising) {
    updateConsent('all');
  } else if (categories.analytics) {
    updateConsent('analytics');
  } else if (categories.advertising) {
    updateConsent('ads');
  } else {
    updateConsent('none');
  }
  syncNonEssentialStorage(categories);
}

export function consentModeLabel(categories = getConsentCategories()) {
  if (categories.analytics && categories.advertising) return 'granted_all';
  if (categories.analytics) return 'analytics_only';
  if (categories.advertising) return 'ads_only';
  return 'denied';
}
